/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1774828800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Mar 30 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "resonate",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Resonate",
          "depth": 0,
          "id": "connect-to-resonate"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Data quality options",
          "depth": 1,
          "id": "data-quality-options"
        }, {
          "value": "File format",
          "depth": 0,
          "id": "file-format"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Match rate",
          "depth": 1,
          "id": "match-rate"
        }, {
          "value": "Data requirements",
          "depth": 1,
          "id": "data-requirements"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      ul: "ul",
      li: "li",
      a: "a",
      em: "em",
      code: "code",
      h3: "h3"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Resonate is an audience intelligence platform that helps brands activate first-party data for targeting and match enrichment. With the Resonate destination, you can sync customer data from your data warehouse to Resonate via SFTP, enabling audience matching and activation."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Sync customer records to Resonate for audience matching"
            }), _jsx(_components.td, {
              children: "All, Insert"
            })]
          })
        })]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "All"
          }), ": Sends all records in your model every time the sync runs."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), ": Sends only new records added since the last sync."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-resonate",
        children: "Connect to Resonate"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Resonate"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "You will need the following credentials from Resonate:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Username"
          }), ": Your Resonate SFTP username."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Private Key"
          }), ": The SSH private key for SFTP authentication. Provide the full private key including the header and footer."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Private Key Passphrase"
          }), " ", _jsx(_components.em, {
            children: "(optional)"
          }), ": The passphrase for your private key, if applicable."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Resonate uses SSH key-based authentication. Contact your Resonate account representative to obtain your SFTP credentials and private key."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch connects to Resonate's SFTP server at ", _jsx(_components.code, {
          children: "sftp.resonate.com"
        }), " on port 22. Files are automatically placed in the ", _jsx(_components.code, {
          children: "data-append/inbound/"
        }), " directory."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Resonate destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button. Select the relevant model and the Resonate destination."]
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Map columns from your model to Resonate's required fields. ", _jsx(_components.strong, {
          children: "Customer ID is required"
        }), " for every sync. You must also include at least one identifier group:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Field"
            }), _jsx(_components.th, {
              children: "Required"
            }), _jsx(_components.th, {
              children: "Notes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Customer ID"
              })
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "Your internal customer identifier"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Email/HEM"
              })
            }), _jsx(_components.td, {
              children: "Pair with HEM Type"
            }), _jsx(_components.td, {
              children: "Emails are automatically hashed (MD5 preferred). Map both HEM and HEM Type together"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "HEM Type"
              })
            }), _jsx(_components.td, {
              children: "Pair with HEM"
            }), _jsx(_components.td, {
              children: "The hash type of the email (for example, MD5, SHA-1, SHA-256)"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "MAID"
              })
            }), _jsx(_components.td, {
              children: "One of the identifier groups"
            }), _jsx(_components.td, {
              children: "Mobile advertising identifier"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Address Line 1"
              })
            }), _jsx(_components.td, {
              children: "All address fields required together"
            }), _jsx(_components.td, {
              children: "Street address"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Address Line 2"
              })
            }), _jsx(_components.td, {
              children: "Optional"
            }), _jsx(_components.td, {
              children: "Secondary address (apartment, suite, etc.)"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "City"
              })
            }), _jsx(_components.td, {
              children: "All address fields required together"
            }), _jsx(_components.td, {
              children: "City"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "State"
              })
            }), _jsx(_components.td, {
              children: "All address fields required together"
            }), _jsx(_components.td, {
              children: "State"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Zip Code"
              })
            }), _jsx(_components.td, {
              children: "All address fields required together"
            }), _jsx(_components.td, {
              children: "5-digit ZIP code"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "ZIP11"
              })
            }), _jsx(_components.td, {
              children: "One of the identifier groups"
            }), _jsx(_components.td, {
              children: "11-digit ZIP+4 code"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "IP Address"
              })
            }), _jsx(_components.td, {
              children: "One of the identifier groups"
            }), _jsx(_components.td, {
              children: "IPv4 format"
            })]
          })]
        })]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "HEM and HEM Type must be mapped together — you cannot map one without the other. Similarly, if you map any postal address field, all required address fields (Address Line 1, City, State, Zip Code) must be mapped."
        })
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Hightouch automatically hashes email values in the HEM field using MD5 (preferred), SHA-1, or SHA-256. You do not need to pre-hash emails before syncing."
        })
      }), "\n", _jsx(_components.h3, {
        id: "data-quality-options",
        children: "Data quality options"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Remove Duplicates"
        }), ": When enabled, duplicate rows based on Customer ID are automatically removed before writing to the file."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Skip Invalid Rows"
        }), ": When enabled, rows missing required identifiers (Customer ID + at least one identifier group) are skipped rather than causing sync errors."]
      }), "\n", _jsx(_components.h2, {
        id: "file-format",
        children: "File format"
      }), "\n", _jsx(_components.p, {
        children: "Resonate requires CSV files. Hightouch automatically handles file naming and placement:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Directory"
          }), ": ", _jsx(_components.code, {
            children: "data-append/inbound/"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "File naming"
          }), ": ", _jsx(_components.code, {
            children: "{ModelName}_{SegmentName}_{YYYYMMDD}_{syncRunId}.csv"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "match-rate",
        children: "Match rate"
      }), "\n", _jsx(_components.p, {
        children: "Resonate targets a 60–80% match rate. To maximize match rates:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Include complete postal address (Address Line 1, City, State, Zip Code) when available — this is Resonate's strongest identifier."
        }), "\n", _jsx(_components.li, {
          children: "Ensure email addresses are lowercase and trimmed before syncing. Hightouch handles hashing automatically."
        }), "\n", _jsx(_components.li, {
          children: "Remove inactive, duplicate, and low-quality records from your model before syncing."
        }), "\n", _jsx(_components.li, {
          children: "Suppress international records, business addresses, and P.O. boxes."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "data-requirements",
        children: "Data requirements"
      }), "\n", _jsx(_components.p, {
        children: "Resonate enforces the following data quality requirements:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Emails must be lowercase with no leading or trailing spaces (Hightouch hashes them automatically)."
        }), "\n", _jsx(_components.li, {
          children: "Records missing consent should be excluded from your model."
        }), "\n", _jsx(_components.li, {
          children: "International records, business addresses, and P.O. boxes should be suppressed."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
